import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get_my_maison_reservations, get_my_maison_analytics, all_reports, get_meta_bookings, get_meta_analytics } from "../../api";
import { selectEndDate, selectStartDate } from "../../reducers/filterSlice";
import { selectUser } from "../../reducers/userSlice";
import { globalAds, globalAll, globalMeta, globalBookings } from "../admin/functionGlobal";
import dayjs from "dayjs";
import SelectBar from "./SelectBar";
import TabLivie from "./tabs/TabLivie/TabLivie";
import TabReservations from "./tabs/TabReservations/TabReservations";
import CustomExportPDF from "../custom/CustomExportPdf";
import CustomExportConversations from "../custom/CustomExportConversations";
import "./tabs/CustomTabs.scss";

const CustomerPanel = ({ adminMode }) => {
    const [tabValue, setTabValue] = useState(null);
    const [property, setProperty] = useState(null);
    const [key, setKey] = useState(0);
    const user = useSelector(selectUser);
    const startDate = useSelector(selectStartDate);
    const endDate = useSelector(selectEndDate);
    const [dateRanges, setDateRanges] = useState([{ start_date: startDate, end_date: endDate }]);
    const params = useParams();
    const [showExportButton, setShowExportButton] = useState(false);

    useEffect(() => {
        if (user) {
            const newProperty = adminMode ? user.properties.find((p) => p.id === parseInt(params.propertyId)) : user.properties[0];
            setProperty(newProperty);
        }
    }, [user, adminMode, params.propertyId]);

    const { data: api, isLoading, error } = useQuery({
        queryKey: ['customerData', property?.id, startDate, endDate],
        queryFn: async () => {
            if (!property || !property.id) {
                return null;
            }
            
            setDateRanges([{ start_date: startDate, end_date: endDate }]);
            
            if (property.id === 92) {
                const dateFormatted = dayjs(startDate).format("YYYY-MM-DD");
                const endDateFormatted = dayjs(endDate).format("YYYY-MM-DD");

                const [allReportsResponse, metaAnalytics, metaBookings] = await Promise.all([
                    all_reports(property.id, dateFormatted, endDateFormatted),
                    get_my_maison_analytics(dateFormatted, endDateFormatted),
                    get_my_maison_reservations(dateFormatted, endDateFormatted)
                ]);

                const processedBookings = metaBookings
                    ? globalBookings(metaBookings).data_0 || []
                    : [];

                const formattedMeta = metaAnalytics ? {
                    ...globalMeta(metaAnalytics),
                    meta_cost: {
                        datesRange: metaAnalytics.metaCost?.map(item => ({
                            cost: item.data?.cost || 0,
                            commission_rate: item.data?.commission_rate || 0,
                            roi: item.data?.roi || 0,
                            start_date: item.range?.start_date,
                            end_date: item.range?.end_date
                        })) || []
                    }
                } : null;

                return {
                    analytics: allReportsResponse.data.ga?.data || null,
                    ads: allReportsResponse.data.ads ? globalAds(allReportsResponse.data.ads) : null,
                    meta: formattedMeta,
                    metaBookings: processedBookings,
                    all: globalAll({
                        meta: metaAnalytics,
                        ads: allReportsResponse.data.ads,
                        ga: allReportsResponse.data.ga
                    }),
                };
            } else {
                const dateFormatted = dayjs(startDate).format("YYYY-MM-DD");
                const endDateFormatted = dayjs(endDate).format("YYYY-MM-DD");

                const allPromises = [];
                
                allPromises.push(all_reports(property.id, dateFormatted, endDateFormatted));
                
                if (property.meta_hotel_code) {
                    allPromises.push(get_meta_analytics(property.meta_hotel_code, dateFormatted, endDateFormatted));
                    allPromises.push(get_meta_bookings(property.meta_hotel_code, dateFormatted, endDateFormatted));
                }

                const results = await Promise.all(allPromises);
                const allReports = results[0];
                const metaAnalytics = results[1] || null;
                const metaBookings = results[2] || null;

                const processedBookings = metaBookings && metaBookings.meta_bookings 
                    ? globalBookings(metaBookings.meta_bookings).data_0 || []
                    : [];
                
                return {
                    analytics: allReports.data.ga?.data || null,
                    ads: allReports.data.ads ? globalAds(allReports.data.ads) : null,
                    meta: metaAnalytics ? globalMeta(metaAnalytics) : null,
                    metaBookings: processedBookings,
                    all: globalAll({
                        meta: metaAnalytics,
                        ads: allReports.data.ads,
                        ga: allReports.data.ga
                    }),
                };
            }
        },
        enabled: !!property && !!startDate && !!endDate,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (api) {
            const hasExportableData =
                (api.analytics && api.analytics.length > 0) ||
                (api.ads && api.ads.data && api.ads.data.some((item) => item.data_0 > 0)) ||
                (api.meta && api.meta.data && api.meta.data.some((item) => item.data_0 > 0)) ||
                (api.all && api.all.some((item) => item.data_0 > 0));
            setShowExportButton(hasExportableData);

            if (tabValue === null) {
                setTabValue(0);
            }
        }
    }, [api, property, tabValue]);

    useEffect(() => {
        if (error) {
            setShowExportButton(false);
            setTabValue(1);
        }
    }, [error]);

    const reloadData = () => {
        setKey((prevKey) => prevKey + 1);
    };

    const handleTabChange = (tabIndex) => {
        setTabValue(tabIndex);
    };

    return (
        <div className="main-component">
            <div className="tab-container">
                <div className="tab-container-left">
                    <div className="tab-container-background">
                        <button
                            className={`tab-link ${tabValue === 0 ? "tab-active" : ""} tab-link-small`}
                            onClick={() => handleTabChange(0)}
                        >
                            <span>Réservations</span>
                        </button>
                        <button className={`tab-link ${tabValue === 1 ? "tab-active" : ""} tab-link-large`} onClick={() => handleTabChange(1)}>
                            <span
                                className={`new-tag ${
                                    tabValue === 1 ? "active-with-reservations" : "default"
                                }`}
                            >
                                New
                            </span>
                            <span>Livie by Altelis</span>
                            <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M4.75247 0.993605C4.8541 0.684585 5.29124 0.684586 5.39287 0.993606L6.4585 4.23378C6.49195 4.33547 6.5717 4.41523 6.67339 4.44868L9.91357 5.51431C10.2226 5.61594 10.2226 6.05308 9.91357 6.15471L6.67339 7.22034C6.5717 7.25379 6.49195 7.33355 6.4585 7.43524L5.39287 10.6754C5.29124 10.9844 4.8541 10.9844 4.75247 10.6754L3.68683 7.43524C3.65339 7.33355 3.57363 7.25379 3.47194 7.22034L0.231765 6.15471C-0.0772553 6.05308 -0.0772546 5.61594 0.231765 5.51431L3.47194 4.44867C3.57363 4.41523 3.65339 4.33547 3.68683 4.23378L4.75247 0.993605Z"
                                    fill="currentColor"
                                />
                                <path
                                    d="M10.5653 8.88694C10.6202 8.72014 10.8561 8.72014 10.911 8.88694L11.4862 10.6359C11.5043 10.6908 11.5473 10.7339 11.6022 10.7519L13.3512 11.3272C13.518 11.382 13.518 11.618 13.3512 11.6728L11.6022 12.248C11.5473 12.2661 11.5043 12.3092 11.4862 12.364L10.911 14.113C10.8561 14.2798 10.6202 14.2798 10.5653 14.113L9.9901 12.364C9.97205 12.3092 9.92899 12.2661 9.8741 12.248L8.1251 11.6728C7.9583 11.618 7.9583 11.382 8.1251 11.3272L9.8741 10.7519C9.92899 10.7339 9.97205 10.6908 9.9901 10.6359L10.5653 8.88694Z"
                                    fill="currentColor"
                                />
                            </svg>
                        </button>
                        <div
                            className={`tab-background ${
                                tabValue === 1
                                    ? "tab-background-livie"
                                    : "tab-background-reservations"
                            }`}
                        ></div>
                    </div>
                </div>
                <div className="tab-container-right">
                    {showExportButton && tabValue === 0 && property && api && (
                        <CustomExportPDF dateRanges={dateRanges} property={property} data={api} />
                    )}
                    {showExportButton && tabValue === 0 && api && <hr />}
                    {tabValue === 1 && property?.livie_id && (
                        <CustomExportConversations startDate={startDate} endDate={endDate} property={property} />
                    )}
                    {showExportButton && tabValue === 1 && property?.livie_id && <hr />}
                    {property?.livie_id || tabValue === 0 ? (
                        <SelectBar loadData={reloadData} dateRanges={dateRanges} hideFlexButton={tabValue === 1} tabLivieAffichage={tabValue === 1} />
                    ) : null}
                </div>
            </div>
            {tabValue === 0 && (
                <div>
                    <TabReservations 
                        key={key} 
                        api={api} 
                        isLoaded={!isLoading} 
                        property={property} 
                        adminMode={user.role === "admin"} 
                    />
                </div>
            )}
            {tabValue === 1 && property && <TabLivie property={property} startDate={startDate} endDate={endDate} isAdmin={user.role === "admin"} />}
        </div>
    );
};

export default CustomerPanel;